import { ReactNode, useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { EarningsSectionCollapseButton } from "./EarningsSectionCollapseButton";
import { EarningsSectionLabel } from "./EarningsSectionLabel";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";

import { formatting } from "@/lib/formatting";

const CARD_WIDTH_PX = 320;
const SCROLL_DISTANCE_PX = CARD_WIDTH_PX + 16;

enum ScrollDirection {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

// Shared section shell: label + commission sum + collapse toggle above a
// horizontally scrolling card rail with edge chevrons.
export const EarningsCardRailSection = ({
  cardCount,
  children,
  commissionTotal,
  label,
}: {
  cardCount: number;
  children: ReactNode;
  commissionTotal: number;
  label: string;
}) => {
  /* React Hooks */
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const cardRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cardRow = cardRowRef.current;

    if (!cardRow) return;

    const updateScrollControls = () => {
      const maximumScrollLeft = cardRow.scrollWidth - cardRow.clientWidth;

      setCanScrollLeft(cardRow.scrollLeft > 1);
      setCanScrollRight(cardRow.scrollLeft < maximumScrollLeft - 1);
    };

    updateScrollControls();
    cardRow.addEventListener("scroll", updateScrollControls, {
      passive: true,
    });
    window.addEventListener("resize", updateScrollControls);

    return () => {
      cardRow.removeEventListener("scroll", updateScrollControls);
      window.removeEventListener("resize", updateScrollControls);
    };
  }, [isCollapsed, cardCount]);

  /* Handlers */
  const handleScroll = (direction: ScrollDirection) => {
    cardRowRef.current?.scrollBy({
      behavior: "smooth",
      left:
        direction === ScrollDirection.RIGHT
          ? SCROLL_DISTANCE_PX
          : -SCROLL_DISTANCE_PX,
    });
  };

  /* Render */
  const renderRail = () => (
    <Row className="relative min-w-0 items-center">
      {canScrollLeft ? (
        <Button
          type="button"
          variant="outlineSecondary"
          size="sm"
          className="absolute -left-4 z-10 h-9 w-9 rounded-full bg-white p-0 shadow-sm"
          onClick={() => {
            handleScroll(ScrollDirection.LEFT);
          }}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
      ) : null}
      <Row
        ref={cardRowRef}
        className="min-w-0 flex-1 items-stretch gap-4 overflow-x-auto scroll-smooth pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </Row>
      {canScrollRight ? (
        <Button
          type="button"
          variant="outlineSecondary"
          size="sm"
          className="absolute -right-4 z-10 h-9 w-9 rounded-full bg-white p-0 shadow-sm"
          onClick={() => {
            handleScroll(ScrollDirection.RIGHT);
          }}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      ) : null}
    </Row>
  );

  const render = () => (
    <Column className="gap-3">
      <Row className="items-center gap-3">
        <EarningsSectionLabel>{label}</EarningsSectionLabel>
        <Text className="self-center text-sm font-semibold tabular-nums leading-none text-green-700">
          ~{formatting.currency(commissionTotal)}
        </Text>
        <EarningsSectionCollapseButton
          isCollapsed={isCollapsed}
          onToggle={() => {
            setIsCollapsed((current) => !current);
          }}
        />
      </Row>
      {isCollapsed ? null : renderRail()}
    </Column>
  );

  return render();
};
